// @flow
import numeral from 'numeral';
import classify from '@src/utils/typestyle';

import {
  Header,
  Key,
  BubbleKey,
  Row,
  CollapsibleRow,
  Rows,
  Section,
  Specifications as List,
  Value,
} from '../Specifications';

import viewHelpers from '@src/viewHelpers';

import * as DiamondDimensionsContent from '../../popups/content/DiamondDimensions';
import * as LengthToWidthRatioContent from '../../popups/content/LengthToWidthRatio';
import * as DepthContent from '../../popups/content/Depth';
import * as TableContent from '../../popups/content/Table';
import * as CuletContent from '../../popups/content/Culet';
import * as GirdleContent from '../../popups/content/Girdle';
import * as CaratWeightContent from '../../popups/content/CaratWeight';
import * as CutContent from '../../popups/content/Cut';
import * as ColorContent from '../../popups/content/Color';
import * as ClarityContent from '../../popups/content/Clarity';
import * as PolishContent from '../../popups/content/Polish';
import * as SymmetryContent from '../../popups/content/Symmetry';
import * as FluorescenceContent from '../../popups/content/Fluorescence';
import * as CertificationContent from '../../popups/content/Certification';
import * as FedExDeliveryContent from '../../popups/content/FedExDelivery';
import * as ReturnPolicyContent from '../../popups/content/ReturnPolicy';
import * as LifetimeTradeInGuaranteeContent from '../../popups/content/LifetimeTradeInGuarantee';
import * as PricePerCaratContent from '../../popups/content/PricePerCarat';

import ColoredFedEx from '../../ColoredFedEx';

import type { DiamondType } from '@types/diamond';

import { linkBlueText } from '@src/styles/general';

const classes = classify({
  imageLink: {
    color: linkBlueText,
    textDecoration: 'underline',
    $nest: {
      '&:hover': {
        color: linkBlueText,
      },
    },
  },
});

const decimalFormat = '0.0[0]';
const twoDecimalFormat = '0.00';
const currencyFormat = '$1,000';

type TProps = {
  diamond: DiamondType,
  onHover: (Name: string) => void,
  omitDeliveryDate?: boolean,
  hideHeader: boolean,
};

export default function Specs({
  diamond,
  onHover,
  omitDeliveryDate,
  hideHeader,
}: TProps) {
  return (
    <List allowExpansion>
      <Section>
        {hideHeader ? null : <Header>Diamond Details</Header>}
        <Rows>
          <Row>
            <Key>Item Number</Key>
            <Value>{diamond.list_service_diamond_id}</Value>
          </Row>
          <Row>
            <Key>Photo / Video</Key>
            <Value>
              {diamond.vendor_image ? (
                <a
                  href={diamond.vendor_image}
                  className={classes.imageLink}
                  target='_blank'
                >
                  {' '}
                  View{' '}
                </a>
              ) : (
                'None'
              )}
            </Value>
          </Row>
          <Row onHover={onHover} hoverKey='length'>
            <BubbleKey content={DiamondDimensionsContent}>Length</BubbleKey>
            <Value>
              {numeral(diamond.measure_length).format(twoDecimalFormat)}
            </Value>
          </Row>
          <Row onHover={onHover} hoverKey='width'>
            <BubbleKey content={DiamondDimensionsContent}>Width</BubbleKey>
            <Value>
              {numeral(diamond.measure_width).format(twoDecimalFormat)}
            </Value>
          </Row>

          <Row onHover={onHover} hoverKey='depth'>
            <BubbleKey content={DiamondDimensionsContent}>Depth</BubbleKey>
            <Value>
              {numeral(diamond.measure_depth).format(twoDecimalFormat)}
            </Value>
          </Row>

          <Row onHover={onHover} hoverKey='lw_ratio'>
            <BubbleKey content={LengthToWidthRatioContent}>L/W Ratio</BubbleKey>
            <Value>
              {numeral(diamond.length_width_ratio).format(twoDecimalFormat)}
            </Value>
          </Row>

          <Row onHover={onHover} hoverKey='depth_percent'>
            <BubbleKey content={DepthContent}>Depth %</BubbleKey>
            <Value>{numeral(diamond.vendor_depth).format(decimalFormat)}</Value>
          </Row>

          <Row onHover={onHover} hoverKey='table_percent'>
            <BubbleKey content={TableContent}>Table %</BubbleKey>
            <Value>{numeral(diamond.vendor_table).format(decimalFormat)}</Value>
          </Row>
          {diamond.vendor_culet && (
            <Row onHover={onHover} hoverKey='culet'>
              <BubbleKey content={CuletContent}>Culet</BubbleKey>
              <Value>
                {diamond.vendor_culet &&
                  diamond.vendor_culet.length &&
                  diamond.vendor_culet}
              </Value>
            </Row>
          )}
          <Row onHover={onHover} hoverKey='girdle'>
            <BubbleKey content={GirdleContent}>Girdle</BubbleKey>
            <Value>
              {viewHelpers.girdleLong(diamond) || (
                <a
                  target='_blank'
                  href={diamond.certificate_url}
                  style={{ color: '#1c487e', textDecoration: 'underline' }}
                >
                  see certificate
                </a>
              )}
            </Value>
          </Row>
          <Row>
            <BubbleKey content={CaratWeightContent}>Carat Weight</BubbleKey>
            <Value>{numeral(diamond.carat).format(twoDecimalFormat)}</Value>
          </Row>
          <Row>
            <BubbleKey content={CutContent}>Cut</BubbleKey>
            <Value>{diamond.final_cut_grade}</Value>
          </Row>
          <Row>
            <BubbleKey content={ColorContent}>Color</BubbleKey>
            <Value>{diamond.color}</Value>
          </Row>
          <Row>
            <BubbleKey content={ClarityContent}>Clarity</BubbleKey>
            <Value>{diamond.clarity}</Value>
          </Row>
          <Row>
            <BubbleKey content={PolishContent}>Polish</BubbleKey>
            <Value>{diamond.vendor_polish}</Value>
          </Row>
          <Row>
            <BubbleKey content={SymmetryContent}>Symmetry</BubbleKey>
            <Value>{diamond.vendor_symmetry}</Value>
          </Row>
          <CollapsibleRow>
            <BubbleKey content={FluorescenceContent}>Fluorescence</BubbleKey>
            <Value>{diamond.vendor_fluorescence}</Value>
          </CollapsibleRow>
          <CollapsibleRow>
            <BubbleKey content={CertificationContent}>
              {' '}
              Certification{' '}
            </BubbleKey>
            <Value>
              {diamond.lab} (
              <a
                target='_blank'
                href={diamond.certificate_url}
                style={{ color: '#1c487e', textDecoration: 'underline' }}
              >
                view
              </a>
              )
            </Value>
          </CollapsibleRow>
          <CollapsibleRow>
            <Key>Total price</Key>
            <Value>
              {numeral(diamond.retail_price).format(currencyFormat)}
            </Value>
          </CollapsibleRow>
          <CollapsibleRow>
            <BubbleKey content={PricePerCaratContent}>
              Price per carat
            </BubbleKey>
            <Value>
              {numeral(diamond.retail_price_per_carat).format(currencyFormat)}
            </Value>
          </CollapsibleRow>
          <CollapsibleRow>
            <BubbleKey content={LifetimeTradeInGuaranteeContent}>
              {' '}
              Trade In{' '}
            </BubbleKey>
            <Value>Lifetime</Value>
          </CollapsibleRow>
          <CollapsibleRow>
            <BubbleKey content={ReturnPolicyContent}>Returns</BubbleKey>
            <Value>30 day full refund</Value>
          </CollapsibleRow>
          {omitDeliveryDate ? null : (
            <CollapsibleRow>
              <BubbleKey content={FedExDeliveryContent}>Shipping</BubbleKey>
              <Value>
                <ColoredFedEx />
              </Value>
            </CollapsibleRow>
          )}
          {omitDeliveryDate ? null : (
            <CollapsibleRow>
              <Key>Delivery Date</Key>
              <Value>{diamond.delivery_date}</Value>
            </CollapsibleRow>
          )}
          <CollapsibleRow>
            <Key>Comes with</Key>
            <Value>
              GIA Certificate
              <br />
              Appraisal
              <br />
              Lifetime Trade-In
              <br />
              Quality Guarantee
              <br />
              Presentation box
            </Value>
          </CollapsibleRow>
        </Rows>
      </Section>
    </List>
  );
}
